import {
    Box,
    CircularProgress,
    IconButton,
    TableCell,
    TableRow,
    Theme,
    Tooltip,
    Typography
} from "@mui/material";
import { BlackDownloadIcon } from "../../../../../assets/SvgIcons";
import {
    useEffect,
    useState
} from "react";
import {
    ColorMap,
    Order,
    SortingObj,
    TableHeaderData
} from "../../../../investorOverview/types";
import CustomTable from "../../../../investorOverview/common/customTable";
import { formatCurrency } from "../../../../../utils/utilityFunctions";
import {
    AmcPerformanceAnalysisProps, AmcPerformanceAnalysisTableData,
    FetchTableDataProps
} from "../types";
import {
    useDispatch,
    useSelector
} from "react-redux";
import { RootStateType } from "../../../../../redux-store/reducers";
import { getAmcPerformanceAnalysis } from "../../../../../redux-store/actions/analytics";
import {
    AmcPerformanceAnalysisData, GetAmcPerformanceAnalysisResponseBody
} from "../../../../../redux-store/types/analytics";
import { reportDownload } from "../../../../Reports/report-utils";
import { amcPerformanceDownload } from "../../../../../redux-store/actions/product";
import { AmcPerformanceAnalysisLoading } from "../../../loadingComponents";
import { NO_DATA_AVAILABLE } from "../../../../../utils/constant";
import { getValueColor } from "../../../../../utils/utilityFunctions";


const tableHeaderData: TableHeaderData[] = [
    {
        id: 'amcname',
        label: 'AMC NAME',
        align: 'left',
        isSortable: false,
    },
    {
        id: 'holding',
        label: 'HOLDING',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'equity',
        label: 'EQUITY',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'debt',
        label: 'DEBT',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'hybrid',
        label: 'HYBRID',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'investedValue',
        label: 'INVESTED VALUE',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'marketValue',
        label: 'MARKET VALUE',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'unRelease',
        label: 'UNREALISED GAIN/ LOSS',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'irr',
        label: 'ABSOLUTE RETURN',
        align: 'center',
        isSortable: true,
    },
]

const colorMap: ColorMap = {
    profit: "success.500",
    loss: "error.500"
}

const assetGroupMap: { [key: string]: string; } = {
    "Mutual Fund": "MF"
}

function calculatePercentage(
    x: string | number,
    y: string | number
): string {
    x = Number(x);
    y = Number(y);

    if (!Number.isFinite(x) || !Number.isFinite(y) || y === 0) {
        return "-";
    }

    return `${Math.floor((x / y) * 100)}%`;
}

function TableBodyData({ data }: {
    data: AmcPerformanceAnalysisData[]
}): JSX.Element {
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    return (
        <>
            {data.map((obj: AmcPerformanceAnalysisData) => {
                const unRealisedGainLossPerc = calculatePercentage(obj?.unRelease, obj?.investedValue)

                return (
                    <TableRow
                        key={obj.amcname}
                        sx={(theme: Theme) => ({
                            '& td': {
                                paddingTop: "15px",
                                paddingBottom: "15px",
                                borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                            },
                            '& td:first-of-type': {
                                paddingLeft: "0px"
                            },
                        })}
                    >
                        <TableCell align="left">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        color: theme.palette.primary[500]
                                    },
                                    // textTransform: 'capitalize'
                                })}
                            >
                                {obj?.amcname ?? "-"}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary
                                })}
                            >
                                {!Number.isNaN(Number(obj?.holding ?? 0)) ?
                                    Math.round((obj?.holding ?? 0) * 100) / 100 : '0'}%
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary
                                })}
                            >
                                {!Number.isNaN(Number(obj?.equity ?? 0)) ?
                                    Math.round((obj?.equity ?? 0) * 100) / 100 : '0'}%
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary
                                })}
                            >
                                {!Number.isNaN(Number(obj?.debt ?? 0)) ?
                                    Math.round((obj?.debt ?? 0) * 100) / 100 : '0'}%
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary
                                })}
                            >
                                {!Number.isNaN(Number(obj?.hybrid ?? 0)) ?
                                    Math.round((obj?.hybrid ?? 0) * 100) / 100 : '0'}%
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary,
                                })}
                            >
                                {(obj?.investedValue ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(obj?.investedValue ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: obj?.investedValue ?? 0, locale, currencyCode })}`}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary,
                                })}
                            >
                                {(obj?.marketValue ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(obj?.marketValue ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: obj?.marketValue ?? 0, locale, currencyCode })}`}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={{
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: (obj?.unRelease ?? 0) >= 0 ? colorMap["profit"] : colorMap["loss"],
                                }}
                            >
                                {obj?.unRelease < 0 ? `- ${formatCurrency({ value: Math.abs(obj?.unRelease), locale, currencyCode })}` : `${formatCurrency({ value: obj?.unRelease, locale, currencyCode })}`}{`(${unRealisedGainLossPerc})`}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={{
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: obj?.irr >= 0 ? colorMap["profit"] : colorMap["loss"],
                                }}
                            >
                                {!Number.isNaN(Number(obj?.irr ?? 0)) ?
                                    Math.round((obj?.irr ?? 0) * 100) / 100 : '0'}%
                            </Typography>
                        </TableCell>
                    </TableRow>
                )
            })}
        </>

    )
}

// Custom Mobile Responsive Body Component for AMC Performance Analysis
// function MobileResponsiveBody({ selectedHeaderKey, data }: { selectedHeaderKey: string, data: AmcPerformanceAnalysisData[] }): JSX.Element {
//     const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

//     const getFormattedValue = (item: AmcPerformanceAnalysisData, key: string) => {
//         switch (key) {
//             case 'holding':
//                 return `${!Number.isNaN(Number(item?.holding ?? 0)) ? Math.round((item?.holding ?? 0) * 100) / 100 : '0'}%`;
//             case 'equity':
//                 return `${!Number.isNaN(Number(item?.equity ?? 0)) ? Math.round((item?.equity ?? 0) * 100) / 100 : '0'}%`;
//             case 'debt':
//                 return `${!Number.isNaN(Number(item?.debt ?? 0)) ? Math.round((item?.debt ?? 0) * 100) / 100 : '0'}%`;
//             case 'hybrid':
//                 return `${!Number.isNaN(Number(item?.hybrid ?? 0)) ? Math.round((item?.hybrid ?? 0) * 100) / 100 : '0'}%`;
//             case 'investedValue':
//                 return (item?.investedValue ?? 0) < 0 ?
//                     `- ${formatCurrency({ value: Math.abs(item?.investedValue ?? 0), locale, currencyCode })}` :
//                     `${formatCurrency({ value: item?.investedValue ?? 0, locale, currencyCode })}`;
//             case 'marketValue':
//                 return (item?.marketValue ?? 0) < 0 ?
//                     `- ${formatCurrency({ value: Math.abs(item?.marketValue ?? 0), locale, currencyCode })}` :
//                     `${formatCurrency({ value: item?.marketValue ?? 0, locale, currencyCode })}`;
//             case 'unRelease': {
//                 const unRealisedGainLossPerc = calculatePercentage(item?.unRelease, item?.investedValue);
//                 return (item?.unRelease ?? 0) < 0 ?
//                     `- ${formatCurrency({ value: Math.abs(item?.unRelease ?? 0), locale, currencyCode })} (${unRealisedGainLossPerc})` :
//                     `${formatCurrency({ value: item?.unRelease ?? 0, locale, currencyCode })} (${unRealisedGainLossPerc})`;
//             }
//             case 'irr':
//                 return `${!Number.isNaN(Number(item?.irr ?? 0)) ? Math.round((item?.irr ?? 0) * 100) / 100 : '0'}%`;
//             default:
//                 return item?.[key as keyof AmcPerformanceAnalysisData] ?? "-";
//         }
//     };

//     return (
//         <>
//             {data.map((item: AmcPerformanceAnalysisData, index: number) => (
//                 <TableRow key={`${item?.amcname ?? ""}-${index}`}>
//                     <TableCell align="left">
//                         <Typography
//                             variant="body_500"
//                             sx={(theme: Theme) => ({
//                                 lineHeight: "20px",
//                                 letterSpacing: "0.02px",
//                                 color: theme.palette.text.primary,
//                                 '&:hover': {
//                                     color: theme.palette.primary[500]
//                                 },
//                             })}
//                         >
//                             {item?.amcname ?? "-"}
//                         </Typography>
//                     </TableCell>
//                     <TableCell align="center">
//                         <Typography
//                             variant="body_500"
//                             sx={(theme: Theme) => ({
//                                 lineHeight: "20px",
//                                 letterSpacing: "0.02px",
//                                 color: theme.palette.text.primary,
//                             })}
//                         >
//                             {getFormattedValue(item, selectedHeaderKey)}
//                         </Typography>
//                     </TableCell>
//                 </TableRow>
//             ))}
//         </>
//     );
// }
function MobileResponsiveBodyAmc({
    data
}: {
    data: AmcPerformanceAnalysisData[]
}): JSX.Element {
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const getFormattedValue = (item: AmcPerformanceAnalysisData, key: string) => {
        switch (key) {
            case 'holding':
                return `${!Number.isNaN(Number(item?.holding ?? 0)) ? Math.round((item?.holding ?? 0) * 100) / 100 : '0'}%`;
            case 'equity':
                return `${!Number.isNaN(Number(item?.equity ?? 0)) ? Math.round((item?.equity ?? 0) * 100) / 100 : '0'}%`;
            case 'debt':
                return `${!Number.isNaN(Number(item?.debt ?? 0)) ? Math.round((item?.debt ?? 0) * 100) / 100 : '0'}%`;
            case 'hybrid':
                return `${!Number.isNaN(Number(item?.hybrid ?? 0)) ? Math.round((item?.hybrid ?? 0) * 100) / 100 : '0'}%`;
            case 'investedValue':
                return (item?.investedValue ?? 0) < 0
                    ? `- ${formatCurrency({ value: Math.abs(item?.investedValue ?? 0), locale, currencyCode })}`
                    : `${formatCurrency({ value: item?.investedValue ?? 0, locale, currencyCode })}`;
            case 'marketValue':
                return (item?.marketValue ?? 0) < 0
                    ? `- ${formatCurrency({ value: Math.abs(item?.marketValue ?? 0), locale, currencyCode })}`
                    : `${formatCurrency({ value: item?.marketValue ?? 0, locale, currencyCode })}`;
            case 'unRelease': {
                const unRealisedGainLossPerc = calculatePercentage(item?.unRelease, item?.investedValue);
                return (item?.unRelease ?? 0) < 0
                    ? `- ${formatCurrency({ value: Math.abs(item?.unRelease ?? 0), locale, currencyCode })} (${unRealisedGainLossPerc})`
                    : `${formatCurrency({ value: item?.unRelease ?? 0, locale, currencyCode })} (${unRealisedGainLossPerc})`;
            }
            case 'irr':
                return `${!Number.isNaN(Number(item?.irr ?? 0)) ? Math.round((item?.irr ?? 0) * 100) / 100 : '0'}%`;
            default:
                return item?.[key as keyof AmcPerformanceAnalysisData] ?? "-";
        }
    };

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {data.map((item, index) => (
                <Box
                    key={`${item?.amcname ?? ""}-${index}`}
                    sx={(theme: Theme) => ({
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: "12px",
                        padding: "16px",
                        backgroundColor: theme.palette.background.paper,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    })}
                >
                    {/* AMC Name */}
                    <Typography
                        variant="body_600"
                        sx={(theme: Theme) => ({
                            color: theme.palette.text.primary,
                            fontSize: "16px",
                            marginBottom: "8px",
                        })}
                    >
                        {item?.amcname ?? "-"}
                    </Typography>

                    {/* AMC Details */}
                    <Box
                        sx={(theme: Theme) => ({
                            display: "grid",
                            gridTemplateColumns: "repeat(1, 1fr)",
                            gap: "12px",
                        })}
                    >
                        {tableHeaderData
                            .filter(header => header.id !== "amcname") // skip AMC name 
                            .map(header => (
                                <Box
                                    key={header.id}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between", // label left, value right
                                        alignItems: "center",
                                    }}
                                >
                                    {/* Label on the left */}
                                    <Typography
                                        variant="overline"
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.text.secondary,
                                            fontSize: "12px",
                                            lineHeight: "16px",
                                        })}
                                    >
                                        {header.label}
                                    </Typography>

                                    {/* Value on the right */}
                                    <Typography
                                        variant="body_500"
                                        sx={(theme: Theme) => ({
                                            color: getValueColor(header.id, Number(item?.[header.id as keyof AmcPerformanceAnalysisData] ?? 0), theme),
                                            fontSize: "14px",
                                            lineHeight: "20px",
                                            textAlign: "right",
                                        })}
                                    >
                                        {getFormattedValue(item, header.id)}
                                    </Typography>
                                </Box>
                            ))}

                    </Box>
                </Box>
            ))}
        </Box>
    );
}


export default function AmcPerformanceAnalysis({
    clientCode,
    portfolioId,
    assetGroup
}: AmcPerformanceAnalysisProps): JSX.Element {
    const dispatch = useDispatch();

    // const [searchText, setSearchText] = useState<string>("")
    const [isLoading, setLoading] = useState<boolean>(false)
    const [tableData, setTableData] = useState<AmcPerformanceAnalysisTableData>({
        data: [],
        isSuccess: true,
        message: ""
    })
    const [tableSortingObj, setTableSortingObj] = useState<SortingObj>({
        order: "asc",
        orderBy: "investedValue"
    })
    const [isDownloading, setDownloading] = useState<boolean>(false)

    const { iwappRmId = "" } = useSelector((state: RootStateType) => state.id);

    const fetchData = async ({
        sortItem = "investedValue",
        order = "asc",
        selectedAssetGroup = "MF",
        clientCode,
        portfolioId
    }: FetchTableDataProps) => {
        try {
            setLoading(true)

            const payload = {
                rmId: `${iwappRmId}`,
                clientCode,
                portfolioId,
                category: "",
                assetGroup: assetGroupMap[selectedAssetGroup],
                sortItem,
                order
            }

            const { result, message, isSuccess } = await (dispatch(getAmcPerformanceAnalysis(payload)) as unknown as Promise<GetAmcPerformanceAnalysisResponseBody>)

            if (isSuccess) {
                setTableData({
                    isSuccess,
                    message,
                    data: result ?? []
                })
            } else {
                setTableData({
                    data: [],
                    isSuccess: false,
                    message: ""
                })
            }

        } catch (error) {
            console.log((error as Error)?.message);
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData({
            clientCode,
            portfolioId,
            selectedAssetGroup: assetGroup
        })
    }, [clientCode,
        portfolioId,
        assetGroup])


    // const handleClearSearch = () => {
    //     setSearchText("")
    // }

    // const handleChangeSearchText = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     setSearchText(event.target.value)
    // }

    const handleSort = (orderBy: string, order: Order) => {
        setTableSortingObj({
            order,
            orderBy
        })

        fetchData({
            sortItem: orderBy,
            order,
            clientCode,
            portfolioId
        })
    }

    const handleDownload = async () => {
        try {
            setDownloading(true);

            const formatExportedData = [
                {
                    "amcname": "AMC NAME",
                    "marketValue": "MARKET VALUE",
                    "equity": "EQUITY",
                    "debt": "DEBT",
                    "hybrid": "HYBRID",
                    "others": "OTHERS",
                    "investedValue": "INVESTED VALUE",
                    "unRelease": "UNREALISED GAIN/LOSS",
                    "release": "REALISED GAIN/LOSS",
                    "dividend": "DIVIDEND",
                    "irr": "ABSOLUTE RETURN",
                    "holding": "HOLDING"
                },
                ...tableData.data
            ]
            const res = await dispatch(amcPerformanceDownload({ jsonData: formatExportedData, sheetName: "amc" })) as unknown as any;
            reportDownload(res as unknown as Blob, 'amcPerformanceInvestorReport.xlsx');
        } catch (e) {
            console.log("Error", e)
        } finally {
            setDownloading(false);
        }
    }

    if (isLoading) {
        return <AmcPerformanceAnalysisLoading />
    }

    if (!isLoading && !tableData?.isSuccess) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        paddingBottom: "20px"
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: '25px',
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                        })}
                    >
                        AMC Performance Analysis
                    </Typography>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        variant="body_600"
                        sx={(theme: Theme) => ({
                            fontWeight: 500,
                            lineHeight: "22.5px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                            textAlign: "center",
                            width: "100%"
                        })}
                    >
                        {NO_DATA_AVAILABLE}
                    </Typography>
                </Box>
            </Box>

        )
    }

    return (
        <Box>
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
            }}>
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        lineheight: "25px",
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })}
                >
                    AMC Performance Analysis
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        columnGap: "25px",
                        flexWrap: "nowrap",
                        justifyContent: "center",
                        alignItems: "center"
                    }}
                >
                    <Tooltip
                        slotProps={{
                            tooltip: {
                                sx: {
                                    margin: "0px !important",
                                    backgroundColor: "transparent",
                                    wordWrap: 'normal'
                                }
                            }
                        }}
                        title={
                            <Box
                                sx={(theme: Theme) => ({
                                    borderRadius: '10px',
                                    backgroundColor: theme.palette.customColors.cinder,
                                    padding: '10px'
                                })}
                            >
                                <Typography
                                    variant="subtitle_500"
                                    component="span"
                                >
                                    Download AMC Performance analysis report
                                </Typography>
                            </Box>
                        }
                    >
                        <IconButton
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                columnGap: "8px",
                                flexWrap: "nowrap",
                                '&:hover': {
                                    backgroundColor: "transparent"
                                },
                                "&:active": {
                                    borderRadius: "0px",
                                }
                            }}
                            onClick={handleDownload}
                            disabled={isDownloading}
                        >
                            {isDownloading && (<CircularProgress size={20} />)}
                            <BlackDownloadIcon />
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    color: theme.palette.text.primary,
                                    lineHeight: "20px",
                                    letterSpacing: "0.01px"
                                })}
                            >
                                Download
                            </Typography>
                        </IconButton>
                    </Tooltip>
                    {/* <TextField
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        sx={{
                                            display: searchText.length > 0 ? 'inline-flex' : 'none',
                                        }}
                                        onClick={handleClearSearch} aria-label="Clear search"
                                    >
                                        <DialogCloseIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: (theme: Theme) => ({
                                borderRadius: '57px',
                                height: '50px',
                                border: `1px solid ${theme.palette.text.secondary}B2`,
                                '& fieldset': {
                                    border: 'none',
                                },
                                '& input': {
                                    paddingLeft: '10px',
                                    ...theme.typography.body_500,
                                    color: theme.palette.text.primary,
                                    '::placeholder': {
                                        ...theme.typography.subtitle_400,
                                        color: theme.palette.text.secondary,
                                        opacity: 1,
                                    },
                                },
                            }),
                        }}
                        placeholder="Search"
                        value={searchText}
                        onChange={handleChangeSearchText}
                    /> */}
                </Box>
            </Box >
            <Box sx={{ marginTop: "20px" }}>
                <CustomTable
                    isLoading={false}
                    tableBody={
                        <TableBodyData data={tableData?.data ?? []} />
                    }
                    tableData={tableData?.data ?? []}
                    tableHeaderData={tableHeaderData}
                    isPaginationVisible={false}
                    tableDataCount={(tableData?.data ?? []).length}
                    handleSort={handleSort}
                    order={tableSortingObj.order}
                    orderBy={tableSortingObj.orderBy}
                    tableBodyLoaderRowCustomStyles={{
                        '& td:first-of-type': {
                            paddingLeft: "0px"
                        },
                    }}
                    tableHeadRowCustomStyles={{
                        '& th:first-of-type': {
                            paddingLeft: "0px"
                        }
                    }}
                    tableBodyCustomStyles={{
                        '& tr:last-child td': {
                            border: 0
                        }
                    }}
                    loadingRowsNum={5}
                    manualResponsiveBody={true}
                    hideResponsiveHeader={true}
                    ManualResponsiveBodyComponent={
                        <MobileResponsiveBodyAmc

                            data={tableData?.data ?? []}
                        />
                    }
                />
            </Box>
        </Box >
    )
}